import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { getSlugRedirect, getContent, getIndustryProducts } from "@/lib/cms.functions";
import { localePath, normalizeLocale } from "@/lib/i18n";
import { pageHead } from "@/content/seo-pages";
import { ArticleView } from "@/components/site/ContentViews";
import { ClosingCta } from "@/components/site/Cta";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { IndustryUseCases } from "@/components/site/IndustryUseCases";
import { IndustryProducts } from "@/components/site/IndustryProducts";
import { industryUseCases } from "@/content/industry-usecases";
import { FaqSection } from "@/components/site/FaqSection";
import { industryFaq } from "@/content/industry-faq";
import { faqScript } from "@/lib/faq-schema";

export const Route = createFileRoute("/{-$locale}/industries/$slug")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const item = await getContent({ data: { kind: "industry", slug: params.slug, locale } });
    if (!item) {
      const current = await getSlugRedirect({ data: { kind: "industry", slug: params.slug, locale } });
      if (current) throw redirect({ href: localePath(locale, `/industries/${encodeURIComponent(current)}`), statusCode: 301 });
      throw notFound();
    }
    const products = await getIndustryProducts({ data: { itemId: item.id, locale } });
    return { locale, item, products };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Page unavailable | factory.online" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { item, locale } = loaderData;
    const head = pageHead(`/industries/${item.slug}`, normalizeLocale(locale), {
      title: `${item.seoTitle} | factory.online`,
      description: item.seoDescription,
    });
    return {
      ...head,
      scripts: faqScript(industryFaq(item.slug), normalizeLocale(locale)),
      meta: head.meta.map((tag) =>
        "property" in tag && tag.property === "og:type"
          ? { property: "og:type", content: "article" }
          : tag,
      ),
    };
  },
  notFoundComponent: IndustriesNotFound,
  component: IndustriesDetail,
});

function IndustriesNotFound() {
  return (
    <NotFoundBlock
      title="Industries entry not found"
      lead="This industry page may have been moved or unpublished."
      backTo="/industries"
      backLabel="Back to Industries"
    />
  );
}

function IndustriesDetail() {
  const { item, locale, products } = Route.useLoaderData();
  const useCases = industryUseCases(item.slug);
  return (
    <>
      <ArticleView item={item} backTo="/industries" backLabel="All industries" locale={locale} showDate={false} />
      <IndustryProducts products={products} industry={item.title} />
      {useCases ? <IndustryUseCases set={useCases} /> : null}
      <FaqSection faqs={industryFaq(item.slug)} title="Questions from manufacturers in this industry" />
      <RelatedLinks variant="industry" currentPath={`/industries/${item.slug}`} />
      <ClosingCta />
    </>
  );
}
